import { useEffect } from 'react';

import { useError } from '@client/hooks/useError';
import { useCreditsQueries } from '@client/queries/useCreditsQueries';
import { type IGrantCreditsPayload } from '@client/services/credits.service';
import { useLoading } from '@client/store/loading.store';
import { EErrorType, type TErrorData } from '@common/types';

interface IUseCreditsFilter {
  tenantId?: string;
  userId?: string;
}

export const useCredits = (filter?: IUseCreditsFilter) => {
  const { useBalance, useLedger, useGrant } = useCreditsQueries();
  const { setError } = useError();

  const {
    data: wallet,
    isLoading: balanceLoading,
    isFetching: balanceFetching,
    refetch: refetchBalance,
  } = useBalance(filter);

  const {
    data: ledgerData,
    isLoading: ledgerLoading,
    isFetching: ledgerFetching,
    refetch: refetchLedger,
  } = useLedger(50, 0, filter);

  const { loading, setLoading } = useLoading(false);

  const isQueryLoading =
    balanceLoading || balanceFetching || ledgerLoading || ledgerFetching;
  useEffect(() => {
    setLoading(isQueryLoading);
  }, [isQueryLoading, setLoading]);

  const grantMutation = useGrant();

  const onGrant = async (
    payload: Omit<IGrantCreditsPayload, 'idempotencyKey'>,
  ): Promise<void> => {
    try {
      setLoading(true);
      await grantMutation.mutateAsync({
        ...payload,
        idempotencyKey: globalThis.crypto.randomUUID(),
      });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_CREATE,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refetch = async (): Promise<void> => {
    await Promise.all([refetchBalance(), refetchLedger()]);
  };

  return {
    loading,
    wallet,
    entries: ledgerData?.entries ?? [],
    total: ledgerData?.total ?? 0,
    onGrant,
    refetch,
  };
};
